import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useDonorAuth } from '../context/DonorAuthContext';
import { Menu, X, Heart, Globe, User } from 'lucide-react';
import { Button } from './ui/button';

const Navigation = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const { isAuthenticated, donor } = useDonorAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/about', label: t.nav.about },
    { to: '/services', label: t.nav.services },
    { to: '/volunteer', label: t.nav.volunteer },
    { to: '/partners', label: t.nav.partners },
    { to: '/events', label: t.nav.events },
    { to: '/shop', label: t.nav.shop },
    { to: '/contact', label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  const donorPortalLabel = language === 'fr' ? 'Espace donateur' : 'Donor Portal';

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_hope-rebuild-2/artifacts/uwaogt8e_C229347E-0B5F-4C4D-9DFB-E23E118FF62E%202.PNG"
              alt="Fondation Enat Logo"
              className="h-24 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  isActive(link.to)
                    ? 'text-[#4A708B] bg-[#4A708B]/10'
                    : 'text-[#546A76] hover:text-[#4A708B] hover:bg-[#4A708B]/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium text-[#546A76] hover:text-[#4A708B] hover:bg-[#4A708B]/5 transition-colors duration-200"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language}</span>
            </button>

            {/* Donor Portal Link */}
            <Link 
              to={isAuthenticated ? "/donor/dashboard" : "/donor/login"}
              data-testid="donor-portal-link"
              className="hidden sm:flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium text-[#546A76] hover:text-[#4A708B] hover:bg-[#4A708B]/5 transition-colors duration-200"
            >
              <User className="w-4 h-4" />
              <span className="hidden md:inline">
                {isAuthenticated ? donor?.name?.split(' ')[0] : donorPortalLabel}
              </span>
            </Link>

            {/* Donate Button */}
            <Link to="/donate" data-testid="nav-donate-btn">
              <Button className="hidden sm:flex gold-btn rounded-full px-6 font-medium">
                {t.nav.donate}
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-full hover:bg-[#4A708B]/10 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#1A252C]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1A252C]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-[#E2E8F0] animate-fade-in"
        >
          <div className="px-4 py-6 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                  isActive(link.to)
                    ? 'text-[#4A708B] bg-[#4A708B]/10'
                    : 'text-[#546A76] hover:text-[#4A708B] hover:bg-[#4A708B]/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {/* Mobile Donor Portal Link */}
            <Link
              to={isAuthenticated ? "/donor/dashboard" : "/donor/login"}
              data-testid="mobile-donor-portal"
              className="block px-4 py-3 rounded-xl text-base font-medium text-[#546A76] hover:text-[#4A708B] hover:bg-[#4A708B]/5 transition-colors"
            >
              <User className="w-4 h-4 inline mr-2" />
              {isAuthenticated ? donor?.name : donorPortalLabel}
            </Link>
            <Link
              to="/donate"
              data-testid="mobile-donate-btn"
              className="block mt-4"
            >
              <Button className="w-full gold-btn rounded-full py-3 font-medium">
                {t.nav.donate}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
